import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Modal, Badge } from 'react-bootstrap';
import { Plus, Star } from 'react-bootstrap-icons';
import axios from 'axios';
import IconPicker from '../../shared/components/IconPicker';
import './BucketList.css';
import { Country, State, City } from 'country-state-city';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const renderIcon = (iconData, size = 24) => {
    if (!iconData) return '📝';
    
    try {
        const icon = typeof iconData === 'string' ? JSON.parse(iconData) : iconData;
        
        if (icon.type === 'emoji') {
            return <span style={{ fontSize: size }}>{icon.value}</span>;
        } else if (icon.type === 'brand') {
            return (
                <svg width={size} height={size} viewBox="0 0 24 24" fill={`#${icon.hex}`}>
                    <path d={icon.path} />
                </svg>
            );
        }
        return '📝';
    } catch {
        return '📝';
    }
};

const BucketList = ({ activeProfileId }) => {
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState({});
    const [completions, setCompletions] = useState({});
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [showCompletionHistory, setShowCompletionHistory] = useState(false);
    const [showEditItemModal, setShowEditItemModal] = useState(false);
    const [showEditCompletionModal, setShowEditCompletionModal] = useState(false);
    const [selectedCompletion, setSelectedCompletion] = useState(null);
    const [editCompletionNotes, setEditCompletionNotes] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [completionDate, setCompletionDate] = useState('');
    const [completionNotes, setCompletionNotes] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    
    // Category form
    const [categoryName, setCategoryName] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [categoryIcon, setCategoryIcon] = useState({ type: 'emoji', value: '📝' });
    
    // Item form
    const [itemTitle, setItemTitle] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [estimatedCost, setEstimatedCost] = useState('');
    const [targetDate, setTargetDate] = useState('');
    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');

    // Edit item form
    const [editItemTitle, setEditItemTitle] = useState('');
    const [editItemDescription, setEditItemDescription] = useState('');
    const [editEstimatedCost, setEditEstimatedCost] = useState('');
    const [editTargetDate, setEditTargetDate] = useState('');
    const [editCountry, setEditCountry] = useState('');
    const [editState, setEditState] = useState('');
    const [editCity, setEditCity] = useState('');

    // Location data
    const [countryData, setCountryData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [cityData, setCityData] = useState([]);
    const [editStateData, setEditStateData] = useState([]);
    const [editCityData, setEditCityData] = useState([]);

    useEffect(() => {
        setCountryData(Country.getAllCountries());
    }, []);

    useEffect(() => {
        if (activeProfileId) {
            fetchCategories();
        }
    }, [activeProfileId]);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/bucket-categories`);
            setCategories(response.data);
            
            for (const category of response.data) {
                fetchItems(category.id);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchItems = async (categoryId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/bucket-categories/${categoryId}/items`);
            setItems(prev => ({ ...prev, [categoryId]: response.data }));
            
            for (const item of response.data) {
                fetchCompletions(item.id);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const fetchCompletions = async (itemId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/bucket-items/${itemId}/completions`);
            setCompletions(prev => ({ ...prev, [itemId]: response.data }));
        } catch (error) {
            console.error('Error fetching completions:', error);
        }
    };

    const handleCreateCategory = async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/bucket-categories`, {
                name: categoryName,
                description: categoryDescription,
                icon: JSON.stringify(categoryIcon)
            });
            setCategories([...categories, response.data]);
            setCategoryName('');
            setCategoryDescription('');
            setCategoryIcon({ type: 'emoji', value: '📝' });
            setShowCategoryModal(false);
        } catch (error) {
            console.error('Error creating category:', error);
        }
    };

    const handleCreateItem = async () => {
        try {
            const countryName = countryData.find(c => c.isoCode === country)?.name;
            const stateName = stateData.find(s => s.isoCode === state)?.name;

            const response = await axios.post(`${API_BASE_URL}/api/life/bucket-categories/${selectedCategory.id}/items`, {
                title: itemTitle,
                description: itemDescription,
                estimated_cost: estimatedCost ? parseFloat(estimatedCost) : null,
                target_date: targetDate || null,
                country: countryName || null,
                state: stateName || null,
                city: city || null
            });
            
            setItems(prev => ({
                ...prev,
                [selectedCategory.id]: [...(prev[selectedCategory.id] || []), response.data]
            }));
            
            setItemTitle('');
            setItemDescription('');
            setEstimatedCost('');
            setTargetDate('');
            setCountry('');
            setState('');
            setCity('');
            setShowItemModal(false);
        } catch (error) {
            console.error('Error creating item:', error);
        }
    };

    const handleToggleItemStatus = async (item) => {
        setSelectedItem(item);
        setCompletionDate(new Date().toISOString().split('T')[0]);
        setShowCompletionModal(true);
    };

    const handleCompleteItem = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/life/bucket-items/${selectedItem.id}/completions`, {
                completed_at: completionDate,
                notes: completionNotes
            });
            
            fetchCompletions(selectedItem.id);
            
            setShowCompletionModal(false);
            setSelectedItem(null);
            setCompletionDate('');
            setCompletionNotes('');
        } catch (error) {
            console.error('Error completing item:', error);
        }
    };

    const handleEditItem = (item) => {
        setSelectedItem(item);
        setEditItemTitle(item.title);
        setEditItemDescription(item.description || '');
        setEditEstimatedCost(item.estimated_cost || '');
        setEditTargetDate(item.target_date ? item.target_date.split('T')[0] : '');
        
        const countryIsoCode = countryData.find(c => c.name === item.country)?.isoCode || '';
        setEditCountry(countryIsoCode);

        if (countryIsoCode) {
            const states = State.getStatesOfCountry(countryIsoCode);
            setEditStateData(states);
            const stateIsoCode = states.find(s => s.name === item.state)?.isoCode || '';
            setEditState(stateIsoCode);
            if (stateIsoCode) {
                setEditCityData(City.getCitiesOfState(countryIsoCode, stateIsoCode) || []);
            } else {
                setEditCityData([]);
            }
        } else {
            setEditStateData([]);
            setEditState('');
            setEditCityData([]);
        }
        
        setEditCity(item.city || '');
        setShowEditItemModal(true);
    };

    const handleUpdateItem = async () => {
        try {
            const countryName = countryData.find(c => c.isoCode === editCountry)?.name;
            const stateName = editStateData.find(s => s.isoCode === editState)?.name;

            const response = await axios.put(`${API_BASE_URL}/api/life/bucket-items/${selectedItem.id}`, {
                title: editItemTitle,
                description: editItemDescription,
                estimated_cost: editEstimatedCost ? parseFloat(editEstimatedCost) : null,
                target_date: editTargetDate || null,
                country: countryName || null,
                state: stateName || null,
                city: editCity || null
            });
            
            setItems(prev => ({
                ...prev,
                [selectedItem.category_id]: prev[selectedItem.category_id].map(i => 
                    i.id === selectedItem.id ? response.data : i
                )
            }));
            
            setShowEditItemModal(false);
            setSelectedItem(null);
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    const handleDeleteItem = async (item) => {
        if (window.confirm(`Are you sure you want to delete "${item.title}"? This action cannot be undone.`)) {
            try {
                await axios.delete(`${API_BASE_URL}/api/life/bucket-items/${item.id}`);
                
                setItems(prev => ({
                    ...prev,
                    [item.category_id]: prev[item.category_id].filter(i => i.id !== item.id)
                }));
            } catch (error) {
                console.error('Error deleting item:', error);
            }
        }
    };

    const handleEditCompletion = (completion) => {
        setSelectedCompletion(completion);
        setEditCompletionNotes(completion.notes || '');
        setShowEditCompletionModal(true);
    };

    const handleUpdateCompletion = async () => {
        try {
            await axios.put(`${API_BASE_URL}/api/life/bucket-completions/${selectedCompletion.id}`, {
                notes: editCompletionNotes
            });
            
            fetchCompletions(selectedItem.id);
            
            setShowEditCompletionModal(false);
            setSelectedCompletion(null);
            setEditCompletionNotes('');
        } catch (error) {
            console.error('Error updating completion:', error);
        }
    };

    return (
        <div className="bucket-list-dashboard">
            <div className="bucket-list-header">
                <h4>Bucket List</h4>
                <Button variant="primary" onClick={() => setShowCategoryModal(true)}>
                    <Plus /> Add Category
                </Button>
            </div>

            {categories.length === 0 ? (
                <div className="text-center py-5">
                    <Star size={48} className="text-muted mb-3" />
                    <h5>Start Your Bucket List</h5>
                    <p className="text-muted">Create categories for your dreams and aspirations!</p>
                    <Button variant="primary" onClick={() => setShowCategoryModal(true)}>
                        Create First Category
                    </Button>
                </div>
            ) : (
                <Row>
                    {categories.map(category => (
                        <Col md={6} lg={4} key={category.id} className="mb-4">
                            <div className="category-card-enhanced">
                                <div className="category-card-header">
                                    <div className="category-card-title">
                                        {renderIcon(category.icon, 24)}
                                        {category.name}
                                    </div>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedCategory(category);
                                            setShowItemModal(true);
                                        }}
                                    >
                                        <Plus size={16} />
                                    </Button>
                                </div>
                                <div className="category-card-body">
                                    {category.description && (
                                        <p className="text-muted small mb-3">{category.description}</p>
                                    )}
                                    
                                    {!items[category.id] || items[category.id].length === 0 ? (
                                        <div className="empty-category-placeholder">
                                            <p>No items yet. Add your first!</p>
                                        </div>
                                    ) : (
                                        <div>
                                            {items[category.id].map(item => (
                                                <div key={item.id} className="bucket-item">
                                                    <div className="bucket-item-content">
                                                        <div className={`bucket-item-title ${completions[item.id] && completions[item.id].length > 0 ? 'completed' : ''}`}>
                                                            {item.title}
                                                        </div>
                                                        {item.description && (
                                                            <div className="bucket-item-description">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                        <div className="bucket-item-meta">
                                                            {item.estimated_cost && (
                                                                <Badge bg="info" className="me-1">${item.estimated_cost}</Badge>
                                                            )}
                                                            {item.target_date && (
                                                                <Badge bg="secondary">{new Date(item.target_date).toLocaleDateString()}</Badge>
                                                            )}
                                                            {item.city && (
                                                                <Badge bg="warning" text="dark">{item.city}, {item.state || item.country}</Badge>
                                                            )}
                                                            {completions[item.id] && completions[item.id].length > 0 && (
                                                                <Badge 
                                                                    bg="success" 
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={() => {
                                                                        setSelectedItem(item);
                                                                        setShowCompletionHistory(true);
                                                                    }}
                                                                >
                                                                    ✓ {completions[item.id].length}x Completed
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="bucket-item-actions">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => handleEditItem(item)}
                                                            title="Edit Item"
                                                        >
                                                            ✏️
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() => handleDeleteItem(item)}
                                                            title="Delete Item"
                                                            className="text-danger"
                                                        >
                                                            🗑️
                                                        </Button>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleToggleItemStatus(item)}
                                                            title="Mark as Completed"
                                                        >
                                                            <Plus size={12} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Modals remain unchanged for now, but could be styled further */}
            <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Bucket List Category</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Category Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                placeholder="e.g., Travel, Adventures, Skills"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={categoryDescription}
                                onChange={(e) => setCategoryDescription(e.target.value)}
                                placeholder="Brief description of this category"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Icon</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowIconPicker(true)}
                                    className="d-flex align-items-center gap-2"
                                >
                                    {renderIcon(categoryIcon, 20)}
                                    Choose Icon
                                </Button>
                                <span className="text-muted small">
                                    {categoryName || 'Preview'} {renderIcon(categoryIcon, 16)}
                                </span>
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>Cancel</Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCreateCategory}
                        disabled={!categoryName.trim()}
                    >
                        Create Category
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showItemModal} onHide={() => setShowItemModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Bucket List Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCategory && (
                        <div className="mb-3">
                            <Badge bg="light" text="dark" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                {renderIcon(selectedCategory.icon, 16)}
                                {selectedCategory.name}
                            </Badge>
                        </div>
                    )}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                                type="text"
                                value={itemTitle}
                                onChange={(e) => setItemTitle(e.target.value)}
                                placeholder="What do you want to do?"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={itemDescription}
                                onChange={(e) => setItemDescription(e.target.value)}
                                placeholder="More details about this item"
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Estimated Cost</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={estimatedCost}
                                        onChange={(e) => setEstimatedCost(e.target.value)}
                                        placeholder="0.00"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Target Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={targetDate}
                                        onChange={(e) => setTargetDate(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Select
                                        value={country}
                                        onChange={(e) => {
                                            setCountry(e.target.value);
                                            setStateData(State.getStatesOfCountry(e.target.value) || []);
                                            setState('');
                                            setCity('');
                                        }}
                                    >
                                        <option value="">Select Country</option>
                                        {countryData.map(c => (
                                            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>State/Province</Form.Label>
                                    <Form.Select
                                        value={state}
                                        onChange={(e) => {
                                            setState(e.target.value);
                                            setCityData(City.getCitiesOfState(country, e.target.value) || []);
                                            setCity('');
                                        }}
                                        disabled={!country}
                                    >
                                        <option value="">Select State</option>
                                        {stateData.map(s => (
                                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <Form.Select
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        disabled={!state}
                                    >
                                        <option value="">Select City</option>
                                        {cityData.map(c => (
                                            <option key={c.name} value={c.name}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowItemModal(false)}>Cancel</Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCreateItem}
                        disabled={!itemTitle.trim()}
                    >
                        Add Item
                    </Button>
                </Modal.Footer>
            </Modal>

            <IconPicker
                show={showIconPicker}
                handleClose={() => setShowIconPicker(false)}
                onSelectIcon={(icon) => setCategoryIcon(icon)}
            />

            <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Completion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <>
                            <p><strong>{selectedItem.title}</strong></p>
                            {completions[selectedItem.id] && completions[selectedItem.id].length > 0 && (
                                <div className="mb-3">
                                    <small className="text-muted">
                                        Previously completed {completions[selectedItem.id].length} time(s)
                                    </small>
                                </div>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>Completion Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={completionDate}
                                    onChange={(e) => setCompletionDate(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Memory/Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={completionNotes}
                                    onChange={(e) => setCompletionNotes(e.target.value)}
                                    placeholder="Share your experience, memories, or thoughts about this completion..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionModal(false)}>Cancel</Button>
                    <Button variant="success" onClick={handleCompleteItem}>Add Completion</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCompletionHistory} onHide={() => setShowCompletionHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Completion History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <>
                            <h6>{selectedItem.title}</h6>
                            <p className="text-muted mb-4">{selectedItem.description}</p>
                            
                            {completions[selectedItem.id] && completions[selectedItem.id].length > 0 ? (
                                <div>
                                    <p><strong>Completed {completions[selectedItem.id].length} time(s):</strong></p>
                                    {completions[selectedItem.id].map((completion, index) => (
                                        <div key={completion.id} className="mb-3 p-3 bg-light rounded">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <Badge bg="success">#{completions[selectedItem.id].length - index}</Badge>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0"
                                                        onClick={() => handleEditCompletion(completion)}
                                                    >
                                                        ✏️
                                                    </Button>
                                                    <small className="text-muted">
                                                        {new Date(completion.completed_at).toLocaleDateString('en-US', {
                                                            weekday: 'long',
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </small>
                                                </div>
                                            </div>
                                            {completion.notes && (
                                                <p className="mb-0 text-muted" style={{fontStyle: 'italic'}}>
                                                    "{completion.notes}"
                                                </p>
                                            )}
                                            {!completion.notes && (
                                                <p className="mb-0 text-muted">
                                                    <em>No notes added</em>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted">No completions yet.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionHistory(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditItemModal} onHide={() => setShowEditItemModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Bucket List Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                                type="text"
                                value={editItemTitle}
                                onChange={(e) => setEditItemTitle(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={editItemDescription}
                                onChange={(e) => setEditItemDescription(e.target.value)}
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Estimated Cost</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={editEstimatedCost}
                                        onChange={(e) => setEditEstimatedCost(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Target Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={editTargetDate}
                                        onChange={(e) => setEditTargetDate(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Select
                                        value={editCountry}
                                        onChange={(e) => {
                                            setEditCountry(e.target.value);
                                            setEditStateData(State.getStatesOfCountry(e.target.value) || []);
                                            setEditState('');
                                            setEditCity('');
                                        }}
                                    >
                                        <option value="">Select Country</option>
                                        {countryData.map(c => (
                                            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>State/Province</Form.Label>
                                    <Form.Select
                                        value={editState}
                                        onChange={(e) => {
                                            setEditState(e.target.value);
                                            setEditCityData(City.getCitiesOfState(editCountry, e.target.value) || []);
                                            setEditCity('');
                                        }}
                                        disabled={!editCountry}
                                    >
                                        <option value="">Select State</option>
                                        {editStateData.map(s => (
                                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <Form.Select
                                        value={editCity}
                                        onChange={(e) => setEditCity(e.target.value)}
                                        disabled={!editState}
                                    >
                                        <option value="">Select City</option>
                                        {editCityData.map(c => (
                                            <option key={c.name} value={c.name}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditItemModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={!editItemTitle.trim()}>
                        Update Item
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditCompletionModal} onHide={() => setShowEditCompletionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Memory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCompletion && (
                        <>
                            <p className="text-muted mb-3">
                                Completed on {new Date(selectedCompletion.completed_at).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                            <Form.Group>
                                <Form.Label>Memory/Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={editCompletionNotes}
                                    onChange={(e) => setEditCompletionNotes(e.target.value)}
                                    placeholder="Share your experience, memories, or thoughts about this completion..."
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditCompletionModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdateCompletion}>
                        Update Memory
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BucketList;
